import {detectDeviceType} from './deviceType'

/**
 * Raw browser facts sent as `metadata.client` with portal analytics events;
 * the subscription backend does all the parsing. node-api forwards only
 * `metadata`, so the client info must live inside it. Async client hints are
 * fetched once per page load and an event never waits for them — until they
 * resolve, `client_hints` is simply null.
 */
let hints: Record<string, unknown> | null = null

type UserAgentData = {
  getHighEntropyValues: (hints: string[]) => Promise<Record<string, unknown>>
}

// Chromium browsers only (Chrome, Edge, Samsung Internet, Opera).
// Safari and Firefox don't support this — that's expected.
try {
  ;(navigator as Navigator & {userAgentData?: UserAgentData}).userAgentData
    ?.getHighEntropyValues(['platformVersion', 'model', 'fullVersionList'])
    .then((h) => {
      hints = {
        platform: h.platform,
        platformVersion: h.platformVersion,
        model: h.model,
        mobile: h.mobile,
        brands: h.brands,
        fullVersionList: h.fullVersionList,
      }
    })
    .catch(() => {})
} catch {
  /* ignore */
}

export function getClientInfo() {
  try {
    return {
      user_agent: navigator.userAgent,
      client_hints: hints,
      screen_w: window.screen.width,
      screen_h: window.screen.height,
      viewport_w: window.innerWidth,
      viewport_h: window.innerHeight,
      pixel_ratio: window.devicePixelRatio,
      touch_points: navigator.maxTouchPoints,
      device_type: detectDeviceType(),
    }
  } catch {
    return null
  }
}

/**
 * Merges `client` into an event's metadata. Some callers pass metadata as
 * null or as a JSON string, so it is parsed safely; a string that isn't JSON
 * is kept as `note`. Never throws — an event is always sent.
 */
export const withClient = (metadata: unknown): Record<string, unknown> => {
  const base =
    typeof metadata === 'string'
      ? (() => {
          try {
            return JSON.parse(metadata)
          } catch {
            return {note: metadata}
          }
        })()
      : metadata || {}
  return {...base, client: getClientInfo()}
}
